'use client'

import { GoNoGoTask as GoNoGoTaskType } from '@/api/services/types/questionTypes'
import * as React from 'react'
import GoNoGoChoice from './GoNoGoChoice'
import { useTestResponse } from '@/hooks/useTestResponse'

export type Signal = 'GO' | 'NO GO' | null

interface GoNoGoTaskProps {
  data?: GoNoGoTaskType
  moduleId: string
  questionId: string
  className?: string
  onComplete?: () => void
  onStart?: () => void
}

interface TaskResult {
  trial: number
  signal: Signal
  responded: boolean
  correct: boolean
  responseTime?: number
}

export function GoNoGoTask({
  data,
  moduleId,
  questionId,
  className,
  onComplete,
  onStart
}: GoNoGoTaskProps) {
  const { addQuestionResponse } = useTestResponse()
  const [currentTrial, setCurrentTrial] = React.useState(0)
  const [currentSignal, setCurrentSignal] = React.useState<Signal>(null)
  const [results, setResults] = React.useState<TaskResult[]>([])
  const [responded, setResponded] = React.useState(false)
  const [stimulusStartTime, setStimulusStartTime] = React.useState<number | null>(null)
  const [remainingTime, setRemainingTime] = React.useState<number>(0)
  const timerRef = React.useRef<NodeJS.Timeout | null>(null)
  const countdownRef = React.useRef<NodeJS.Timeout | null>(null)
  React.useEffect(() => {
    startTask()
  }, [])

  // Używamy danych z API lub domyślnych wartości
  const totalTrials = data ? data.goStimuliCount + data.noGoStimuliCount : 30
  const getStimulusDuration = () => (data ? data.responseTimeSeconds * 1000 : 5000)
  const stimuli = data?.stimuli || []

  const startTask = () => {
    onStart?.()
    setCurrentTrial(0)
    setResults([])
    showNextStimulus()
  }

  const showNextStimulus = () => {
    // Clear any existing timers
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
    }

    // Używamy sekwencji z API lub losujemy
    const currentStimulus = stimuli[currentTrial]
    const signal: Signal = currentStimulus.isGoStimulus ? 'GO' : 'NO GO'
    const duration = getStimulusDuration()

    setCurrentSignal(signal)
    setResponded(false)
    setStimulusStartTime(Date.now())
    setRemainingTime(duration)

    // Start countdown
    countdownRef.current = setInterval(() => {
      setRemainingTime((prev) => Math.max(0, prev - 100))
    }, 100)

    // Set timer to move to next trial after stimulusDuration
    timerRef.current = setTimeout(async () => {
      if (!responded) {
        // Record non-response
        const correct = signal === 'NO GO'
        setResults((prev) => [
          ...prev,
          {
            trial: currentTrial,
            signal,
            responded: false,
            correct
          }
        ])
      }

      setCurrentTrial((prev) => prev + 1)
      if (currentTrial < totalTrials) {
        showNextStimulus()
      } else {
        setCurrentSignal(null)

        // Zapisz odpowiedzi do TestResponseProvider
        await addQuestionResponse(moduleId, {
          questionId,
          questionType: 'GO_NO_GO_TASK',
          goNoGoTaskAnswer: {
            responses: results.map((result) => ({
              stimulusIndex: result.trial,
              responded: result.responded,
              responseTimeMillis: result.responseTime || 0
            }))
          }
        })

        onComplete?.()
      }
    }, duration)
  }

  const handleResponse = async () => {
    if (responded || !stimulusStartTime) return

    const responseTime = Date.now() - stimulusStartTime
    const correct = currentSignal === 'GO'

    setResponded(true)
    setResults((prev) => [
      ...prev,
      {
        trial: currentTrial,
        signal: currentSignal,
        responded: true,
        correct,
        responseTime
      }
    ])

    // Wyczyść timery i przejdź do następnego bodźca
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
    }
    if (currentTrial < totalTrials) {
      setCurrentTrial((prev) => prev + 1)
      showNextStimulus()
    } else {
      setCurrentSignal(null)

      // Zapisz odpowiedzi do TestResponseProvider
      await addQuestionResponse(moduleId, {
        questionId,
        questionType: 'GO_NO_GO_TASK',
        goNoGoTaskAnswer: {
          responses: results.map((result) => ({
            stimulusIndex: result.trial,
            responded: result.responded,
            responseTimeMillis: result.responseTime || 0
          }))
        }
      })

      onComplete?.()
    }
  }

  return (
    <div className={className}>
      {
        <GoNoGoChoice
          currentSignal={currentSignal}
          currentTrial={currentTrial}
          handleResponse={handleResponse}
          remainingTime={remainingTime}
          responded={responded}
          totalTrials={totalTrials}
        />
      }
    </div>
  )
}
